import { useState } from 'react';
import { LogOut, Search, ChevronRight, Building2, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { useLocation } from 'react-router-dom';
import { MobileMenuButton } from './Sidebar';
import { CompanyContextSwitcher } from '@/components/CompanyContextSwitcher';
import { useCompanyContextStore } from '@/store/company-context-store';
import { isFunctionalPath, themeFor, useThemeStore } from '@/lib/theme';

interface HeaderProps {
  onMenuClick: () => void;
}

// Map routes to breadcrumb labels
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/entity-registry': 'Entity_Registry',
  '/mappings': 'Mappings',
  '/schemas': 'Schemas',
  '/lookup-tables': 'Lookup_Tables',
  '/connections': 'Connections',
  '/workflows': 'Workflows',
  '/dead-letter-queues': 'Dead_Letter_Queues',
  '/users': 'Users',
  '/companies': 'Companies',
  '/settings': 'Settings',
  '/quota': 'Quota_Usage',
  '/audit-trail': 'Audit_Trail',
  '/integration-audit': 'Integration_Audit',
  '/demo-inbox': 'Demo_Inbox',
  '/systems': 'Systems',
  '/setup': 'Setup',
};

// Business modules speak plain Spanish (functional theme).
const FUNCTIONAL_LABELS: Record<string, string> = {
  '/data-hub': 'Panorama',
  '/data-hub/dashboards': 'Dashboards',
  '/data-hub/explore': 'Explorar',
  '/data-hub/reports': 'Reportes',
  '/data-hub/agents': 'Agentes IA',
  '/data-hub/power-bi': 'Power BI',
  '/data-hub/tables': 'Tablas',
  '/documents': 'Bandeja',
  '/documents/instructions': 'Instrucciones',
};

function functionalLabel(path: string): { module: string; page: string } {
  const module = path.startsWith('/documents') ? 'Documentos' : 'Data Hub';
  const page = FUNCTIONAL_LABELS[path];
  if (page) return { module, page };
  if (path.startsWith('/documents/')) return { module, page: 'Revisión' };
  return { module, page: '' };
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, isAuthenticated, signOut, permissions } = useAuth();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const companyName = useCompanyContextStore((s) => s.companyName);
  const isSuperAdmin = permissions.canManageSystem;
  const functional = isFunctionalPath(location.pathname);
  const crumb = functionalLabel(location.pathname);
  const pref = useThemeStore((s) => s.preference);
  const setPref = useThemeStore((s) => s.setPreference);
  const light = themeFor(location.pathname, pref) === 'functional';

  // Get current page label from route
  const getPageLabel = () => {
    const path = location.pathname;
    // Check for exact match first
    if (ROUTE_LABELS[path]) return ROUTE_LABELS[path];
    // Check for partial match (e.g., /entity-registry/something)
    for (const [route, label] of Object.entries(ROUTE_LABELS)) {
      if (path.startsWith(route) && route !== '/') return label;
    }
    return 'Dashboard';
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-header px-4 lg:px-6">
      {/* Left side - Mobile menu and breadcrumb */}
      <div className="flex items-center gap-3">
        <MobileMenuButton onClick={onMenuClick} />

        {functional ? (
          /* Breadcrumb in plain language (business modules) */
          <div className="hidden items-center gap-1.5 text-sm sm:flex">
            {companyName && !isSuperAdmin && (
              <>
                <span className="font-medium text-subtle">{companyName}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </>
            )}
            <span className={crumb.page ? 'font-medium text-subtle' : 'font-semibold text-foreground'}>{crumb.module}</span>
            {crumb.page && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-semibold text-foreground">{crumb.page}</span>
              </>
            )}
          </div>
        ) : (
          /* Breadcrumb navigation (technical console) */
          <div className="hidden items-center gap-1.5 text-xs sm:flex">
            {companyName && !isSuperAdmin ? (
              <>
                <Building2 className="h-3 w-3 text-primary" />
                <span className="font-medium text-primary">{companyName.toUpperCase()}</span>
              </>
            ) : (
              <span className="text-muted-foreground">SYS</span>
            )}
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium text-foreground">{getPageLabel().toUpperCase()}</span>
          </div>
        )}

        {/* Mobile title */}
        <span className="ui-caps text-xs font-medium uppercase tracking-wide text-foreground sm:hidden">
          Integration Studio
        </span>
      </div>

      {/* Right side - Company context, Search and Sign out */}
      <div className="flex items-center gap-3">
        {isAuthenticated && user && (
          <>
            {/* Company context switcher (super-admin only) */}
            <CompanyContextSwitcher />

            {/* Theme switch (applies to the whole portal) */}
            <button
              onClick={() => setPref(light ? 'dark' : 'light')}
              className="flex h-8 w-8 items-center justify-center rounded border border-border text-subtle transition-colors hover:bg-foreground/5 hover:text-foreground"
              title={light ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro'}
              aria-label={light ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro'}
            >
              {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>

            {/* Search bar */}
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={functional ? 'Buscar...' : 'SEARCH...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ui-field h-8 w-48 rounded border border-input bg-foreground/5 pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground transition-colors focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
            </div>

            {/* Sign out button */}
            <button
              onClick={signOut}
              className="ui-button ui-caps flex items-center gap-2 rounded border border-border px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-subtle transition-colors hover:border-border-strong hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{functional ? 'Salir' : 'Sign_Out'}</span>
            </button>
          </>
        )}
      </div>
    </header>
  );
}
